import { action, computed, makeObservable, reaction, IReactionDisposer } from "mobx";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { buildOnboardingContext } from "./onboarding-context";
import { ONBOARDING_STEP_DEFINITIONS } from "./onboarding-step-definitions";
import type { MDSIconKind } from "@/mds/components/icon/icon/icon.types";
import type {
  OnboardingContext,
  OnboardingStepDefinition,
  OnboardingStepId,
  OnboardingSubstepId,
} from "./onboarding-types";

export type OnboardingStepperStatus = "completed" | "active" | "upcoming";

export interface OnboardingStepperStep {
  id: OnboardingStepId;
  icon: MDSIconKind;
  status: OnboardingStepperStatus;
}

/**
 * Centralized onboarding store.
 *
 * Derives all onboarding state from a single ordered step pipeline.
 * Each step declares when it is applicable and when it is completed,
 * so the store can compute currentStep, progress, and overlay visibility
 * without imperative navigation logic.
 */
export class OnboardingStore extends AppSubStore {
  private reactionDisposers: IReactionDisposer[] = [];

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<OnboardingStore, "reactionDisposers">(this, {
      reactionDisposers: false,
      dispose: false,
      context: computed,
      allSteps: computed,
      applicableSteps: computed,
      pendingSteps: computed,
      completedSteps: computed,
      currentStep: computed,
      currentStepId: computed,
      currentSubstepId: computed,
      hasNextStep: computed,
      showFinish: computed,
      isActive: computed,
      progress: computed,
      stepperSteps: computed,
      completeCurrentStep: action,
      debugResetAll: action,
      initialize: action,
    });
  }

  /** Sets up tracking reactions for onboarding state transitions. */
  async initialize() {
    this.dispose();

    this.reactionDisposers.push(
      reaction(
        () => this.currentStepId,
        (toStepId: OnboardingStepId | undefined, fromStepId: OnboardingStepId | undefined) => {
          if (toStepId === undefined && fromStepId !== undefined) {
            trackEvent(TrackedEvent.OnboardingComplete, {
              total_steps: this.applicableSteps.length,
            });
          } else {
            trackEvent(TrackedEvent.OnboardingStepChange, {
              from_step_id: fromStepId,
              to_step_id: toStepId,
              completed_count: this.completedSteps.length,
              total_count: this.applicableSteps.length,
            });
          }
        }
      ),
      reaction(
        () => this.currentSubstepId,
        (
          toSubstepId: OnboardingSubstepId | undefined,
          fromSubstepId: OnboardingSubstepId | undefined
        ) => {
          trackEvent(TrackedEvent.OnboardingSubstepChange, {
            step_id: this.currentStepId,
            from_substep_id: fromSubstepId,
            to_substep_id: toSubstepId,
          });
        }
      )
    );
  }

  /** Tears down tracking reactions. */
  dispose() {
    this.reactionDisposers.forEach(d => d());
    this.reactionDisposers = [];
  }

  /** Snapshot of all state the step predicates need. */
  get context(): OnboardingContext {
    return buildOnboardingContext(this.store);
  }

  /** All step definitions (including non-applicable). Used by debug overlay. */
  get allSteps(): OnboardingStepDefinition[] {
    return ONBOARDING_STEP_DEFINITIONS;
  }

  /** Steps that are applicable given the current context. */
  get applicableSteps(): OnboardingStepDefinition[] {
    return ONBOARDING_STEP_DEFINITIONS.filter(step => step.applicableWhen(this.context));
  }

  /** Applicable steps that are not yet completed. */
  get pendingSteps(): OnboardingStepDefinition[] {
    const ctx = this.context;
    return this.applicableSteps.filter(step => !step.completedWhen(ctx));
  }

  /** Applicable steps that have been completed. */
  get completedSteps(): OnboardingStepDefinition[] {
    const ctx = this.context;
    return this.applicableSteps.filter(step => step.completedWhen(ctx));
  }

  /** The first applicable step that is not yet completed, or undefined if all done. */
  get currentStep(): OnboardingStepDefinition | undefined {
    return this.pendingSteps[0];
  }

  /** The ID of the current step, or undefined if all done. */
  get currentStepId(): OnboardingStepId | undefined {
    return this.currentStep?.id;
  }

  /** The wizard substep ID to display. Resolves via substep activeWhen predicates. */
  get currentSubstepId(): OnboardingSubstepId | undefined {
    const step = this.currentStep;
    if (!step) return undefined;

    const match = step.substeps.find(s => s.activeWhen(this.context));
    return match?.id ?? step.substeps[0].id;
  }

  /** Whether there is another step after the current one. */
  get hasNextStep(): boolean {
    return this.pendingSteps.length > 1;
  }

  /**
   * Whether the current substep is the final exit point of the entire
   * onboarding pipeline. Components use this to show "Finish" vs "Next".
   */
  get showFinish(): boolean {
    const step = this.currentStep;
    if (!step) return false;

    if (this.hasNextStep) return false;

    // The active substep is the last one in the definition array
    const activeSubstep = step.substeps.find(s => s.activeWhen(this.context));
    const lastSubstep = step.substeps[step.substeps.length - 1];
    return activeSubstep?.id === lastSubstep.id;
  }

  /** Whether onboarding is active (has an incomplete step). */
  get isActive(): boolean {
    return this.currentStep !== undefined;
  }

  /** Progress as a fraction [0, 1]. */
  get progress(): number {
    const total = this.applicableSteps.length;
    if (total === 0) return 1;
    return this.completedSteps.length / total;
  }

  /** Steps that participate in the stepper UI, with their completion status. */
  get stepperSteps(): OnboardingStepperStep[] {
    const ctx = this.context;
    const currentId = this.currentStepId;
    let foundCurrent = false;

    return this.applicableSteps
      .filter(
        (step): step is OnboardingStepDefinition & { stepperIcon: MDSIconKind } =>
          step.stepperIcon !== undefined
      )
      .map(step => {
        if (step.completedWhen(ctx)) {
          return { id: step.id, icon: step.stepperIcon, status: "completed" as const };
        }
        if (step.id === currentId && !foundCurrent) {
          foundCurrent = true;
          return { id: step.id, icon: step.stepperIcon, status: "active" as const };
        }
        return { id: step.id, icon: step.stepperIcon, status: "upcoming" as const };
      });
  }

  /**
   * Complete the current step by calling its declarative complete action.
   *
   * Each step definition carries its own complete function, so this method
   * simply delegates — no switch statement needed.
   */
  async completeCurrentStep(completedSteps: string[] = []) {
    const step = this.currentStep;
    if (!step) return;
    await step.complete(this.store, completedSteps);
  }

  /** Reset all applicable steps that have a debugReset handler. */
  async debugResetAll() {
    const ctx = this.context;
    await Promise.all(
      this.allSteps
        .filter(step => step.applicableWhen(ctx) && step.debugReset)
        .map(step => step.debugReset!(this.store))
    );
  }
}
